import { useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Feedback, useResource } from "@/components/workflow/shared";
import { siteEquipmentApi } from "@/lib/api/oprema";
import { equipmentE2Api } from "@/lib/api/oprema-e2";
import { useEquipmentAccess } from "@/components/site-equipment/shared";
import { E2DocumentActions } from "@/components/site-equipment/E2Shared";
import { RentalViews } from "@/components/site-equipment/RentalViews";
import { InventoryViews } from "@/components/site-equipment/InventoryViews";
import {
  ClosureView,
  RequestViews,
} from "@/components/site-equipment/ClosureRequests";
import { EquipmentBack } from "@/components/site-equipment/EquipmentNavigation";

export function EquipmentE2Page({
  screen,
}: {
  screen: "rental" | "inventory" | "closure" | "requests" | "documents";
}) {
  const { id } = useParams(),
    access = useEquipmentAccess();
  const [params] = useSearchParams();
  const title = {
    rental: "Najam",
    inventory: "Inventura",
    closure: "Zatvaranje gradilišta",
    requests: "Zatraži iz Baze",
    documents: "Zapisnici",
  }[screen];
  const resource = useResource(
    `e2-lookup:${access.read}:${access.maintain}:${id}`,
    useCallback(
      async (signal) => {
        if (!access.read) return null;
        const [articles, places] = await Promise.all([
          siteEquipmentApi.articles(signal),
          siteEquipmentApi.places(signal, access.maintain),
        ]);
        return { articles, places };
      },
      [access.read, access.maintain],
    ),
  );
  if (!access.read) return <p role="alert">Nemate ovlast za pregled opreme.</p>;
  return (
    <div className="site-equipment">
      {!params.has("inventura") && (
        <EquipmentBack
          crumbs={[
            ...(id
              ? [{
                  label: resource.data?.places.find((p) => p.id === id)?.naziv ?? "Mjesto",
                  to: `/oprema/mjesta/${id}`,
                }]
              : []),
            { label: title },
          ]}
        />
      )}
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      {resource.loading && <p role="status">Učitavanje opreme…</p>}
      {resource.data && (
        <>
          {screen === "rental" ? (
            <RentalViews lookup={resource.data} />
          ) : screen === "inventory" ? (
            <InventoryViews placeId={id} lookup={resource.data} />
          ) : screen === "closure" && id ? (
            <ClosureView placeId={id} />
          ) : screen === "requests" ? (
            <RequestViews lookup={resource.data} placeId={id} />
          ) : (
            <DocumentsView placeId={id} />
          )}
        </>
      )}
    </div>
  );
}
function DocumentsView({ placeId }: { placeId?: string }) {
  const resource = useResource(
    `e2-documents:${placeId}`,
    useCallback(
      (signal) => equipmentE2Api.documents(placeId, signal),
      [placeId],
    ),
  );
  return (
    <section className="equipment-form">
      <h2 className="text-xl font-bold">Zapisnici opreme</h2>
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje zapisnika…</p>}
      {resource.data?.map((document) => (
        <E2DocumentActions document={document} key={document.id} />
      ))}
      {resource.data?.length === 0 && <p>Nema zapisnika.</p>}
    </section>
  );
}
